import { jwtDecode } from 'jwt-decode';
import { getToken, removeToken, clearUser } from './auth';

// Fim de sessão num lugar só: limpa o navegador, guarda a página em que a pessoa
// estava e o aviso que a tela de login mostra, e recarrega no /login (nenhum dado
// da sessão antiga fica na tela).
const CHAVE_AVISO = 'sisagenda:aviso-login';
const CHAVE_VOLTAR = 'sisagenda:voltar-para';

const PAGINAS_PUBLICAS = [
  '/', '/login', '/tutorial', '/confirmar-email', '/em-desenvolvimento', '/esqueci-senha', '/redefinir-senha',
];

const AVISOS = {
  expirada: 'Sua sessão expirou. Entre novamente para continuar de onde parou.',
  invalida: 'Sua sessão não é mais válida. Entre novamente para continuar.',
  outraAba: 'Você saiu do sistema em outra aba.',
};

export const paginaPublica = (pathname = window.location.pathname) => PAGINAS_PUBLICAS.includes(pathname);

export const paginaInicialDoPapel = (role) =>
  ({ ADMIN: '/admin', COORDENADOR: '/coordenador' }[role] || '/dashboard');

// Usuário salvo no navegador mesmo com o token vencido (o getUser() já descarta).
const usuarioSalvo = () => {
  try {
    return JSON.parse(localStorage.getItem('user') || 'null');
  } catch {
    return null;
  }
};

// Segundos até o token vencer; negativo se venceu ou não dá para ler.
export const segundosParaExpirar = (token = getToken()) => {
  if (!token) return -1;
  try {
    const { exp } = jwtDecode(token);
    return exp ? exp - Date.now() / 1000 : Infinity;
  } catch {
    return -1;
  }
};

const guardar = (chave, valor) => {
  try {
    if (valor === null) sessionStorage.removeItem(chave);
    else sessionStorage.setItem(chave, valor);
  } catch {
    // sessionStorage bloqueado: segue sem o aviso e sem voltar à página.
  }
};

const ler = (chave) => {
  try {
    const valor = sessionStorage.getItem(chave);
    sessionStorage.removeItem(chave);
    return valor;
  } catch {
    return null;
  }
};

let encerrando = false;

// motivo: 'expirada' | 'invalida' | 'outraAba' mostram aviso no login; 'saiu' é o botão Sair.
// Devolve true quando vai recarregar a página (quem chamou não deve mostrar mais nada).
export const encerrarSessao = (motivo) => {
  if (encerrando) return true;

  const usuario = usuarioSalvo();
  removeToken();
  clearUser();
  window.dispatchEvent(new Event('userUpdated'));

  // Numa página pública (início, tutorial...) só limpa: não tira a pessoa de onde está.
  if (motivo !== 'saiu' && paginaPublica()) return false;

  encerrando = true;
  const { pathname, search } = window.location;
  if (AVISOS[motivo]) {
    guardar(CHAVE_AVISO, AVISOS[motivo]);
    guardar(CHAVE_VOLTAR, JSON.stringify({ caminho: pathname + search, usuarioId: usuario?.id ?? null }));
  } else {
    guardar(CHAVE_AVISO, null);
    guardar(CHAVE_VOLTAR, null);
  }
  window.location.assign('/login');
  return true;
};

// Só lê: o aviso continua lá se a tela de login montar de novo (ou for recarregada)
// e sai quando a pessoa entra (destinoDepoisDoLogin).
export const lerAvisoDeLogin = () => {
  try {
    return sessionStorage.getItem(CHAVE_AVISO);
  } catch {
    return null;
  }
};

// Depois do login, volta para onde a pessoa estava: a página em que a sessão caiu
// (se for a mesma pessoa) ou a página protegida que ela tentou abrir sem estar logada.
export const destinoDepoisDoLogin = (usuario, tentouAbrir) => {
  guardar(CHAVE_AVISO, null);
  try {
    const salvo = JSON.parse(ler(CHAVE_VOLTAR) || 'null');
    if (salvo?.caminho && salvo.usuarioId === usuario.id && !paginaPublica(salvo.caminho.split('?')[0])) {
      return salvo.caminho;
    }
  } catch {
    // valor antigo ou corrompido: segue para as outras opções
  }
  if (tentouAbrir?.pathname && !paginaPublica(tentouAbrir.pathname)) {
    return tentouAbrir.pathname + (tentouAbrir.search || '');
  }
  return paginaInicialDoPapel(usuario.role);
};
